import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { createMyWorkspaceViewModel } from "../lib/myWorkspace";
import type { ActionItem, DraftItem, FollowedItem, LexData } from "../lib/types";

type Tab = "actions" | "following" | "drafts" | "proc";

export function MyWorkspacePage({ data }: { data: LexData; mode?: "convex" | "demo" }) {
  const [searchParams] = useSearchParams();
  const workspace = useMemo(() => createMyWorkspaceViewModel(data), [data]);
  const viewParam = searchParams.get("view");
  const [tab, setTab] = useState<Tab>(() => tabForView(viewParam));

  // Quick access links (Saved & followed / Drafts / Votes due) change only the
  // `?view=` query param. When the user is already on this page the route does
  // not remount, so keep the active tab in sync with the URL.
  useEffect(() => {
    setTab(tabForView(viewParam));
  }, [viewParam]);

  const procurations = data.delegations ?? [];

  return (
    <>
      <div className="phead">
        <div className="eyebrow">My workspace</div>
        <h1 className="h1 serif">Your actions &amp; saved work</h1>
        <p className="lead">Everything assigned to you or saved for later — votes due, conflict checks, drafts, followed threads and the procurations you hold.</p>
      </div>

      <div className="card">
        <div className="tabs">
          <button type="button" className={`tab${tab === "actions" ? " tab-on" : ""}`} onClick={() => setTab("actions")}>Actions · {workspace.action_items.length}</button>
          <button type="button" className={`tab${tab === "following" ? " tab-on" : ""}`} onClick={() => setTab("following")}>Saved &amp; followed · {workspace.followed_items.length}</button>
          <button type="button" className={`tab${tab === "drafts" ? " tab-on" : ""}`} onClick={() => setTab("drafts")}>Drafts · {workspace.drafts.length}</button>
          <button type="button" className={`tab${tab === "proc" ? " tab-on" : ""}`} onClick={() => setTab("proc")}>Procurations · {procurations.length}</button>
        </div>
        <div style={{ height: 1, background: "var(--line)", marginTop: 12 }} />

        {tab === "actions" ? (
          workspace.action_items.length > 0 ? workspace.action_items.map((item) => <ActionRow key={item.id} item={item} />) : <EmptyRow>No actions need your attention right now.</EmptyRow>
        ) : null}

        {tab === "following" ? (
          workspace.followed_items.length > 0 ? workspace.followed_items.map((item) => <FollowingRow key={item.id} item={item} />) : <EmptyRow>You are not following anything yet.</EmptyRow>
        ) : null}

        {tab === "drafts" ? (
          workspace.drafts.length > 0 ? workspace.drafts.map((item) => <DraftRow key={item.id} item={item} />) : <EmptyRow>No drafts yet. Start a question, proposal or resource from Create.</EmptyRow>
        ) : null}

        {tab === "proc" ? (
          <div className="attn">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row-title">Procurations</div>
              <div className="row-meta">{procurations.length > 0 ? `${procurations.length} active` : "No procurations are currently held or granted."}</div>
            </div>
            <Link className="btn-sm" to="/my-workspace/delegations">Manage</Link>
          </div>
        ) : null}
      </div>
    </>
  );
}

function ActionRow({ item }: { item: ActionItem }) {
  const tone = actionTone(item.type);
  return (
    <div className="attn">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
          <span className={`pill ${tone.pill}`}>{item.type}</span>
        </div>
        <div className="row-title">{item.title}</div>
        <div className="row-meta">{item.module} · {item.status}</div>
      </div>
      <Link className={`btn-sm${tone.primary ? " btn-primary" : ""}`} to={item.action_href ?? "/my-workspace?view=actions"}>{item.action_label}</Link>
    </div>
  );
}

function FollowingRow({ item }: { item: FollowedItem }) {
  const external = item.action_href.startsWith("http");
  const inner = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
          <span className={`pill ${pillForKind(item.item_type)}`}>{item.item_type}</span>
        </div>
        <div className="row-title">{item.title}</div>
        <div className="row-meta">{item.status}</div>
      </div>
      <span className="btn-sm">Open</span>
    </>
  );
  return external ? (
    <a className="attn" href={item.action_href} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>{inner}</a>
  ) : (
    <Link className="attn" to={item.action_href} style={{ textDecoration: "none" }}>{inner}</Link>
  );
}

function DraftRow({ item }: { item: DraftItem }) {
  return (
    <div className="attn">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
          <span className={`pill ${pillForKind(item.post_type)}`}>{item.post_type}</span>
        </div>
        <div className="row-title">{item.title}</div>
        <div className="row-meta">Draft · last edited {relativeAge(item.last_edited_at)}</div>
      </div>
      <Link className="btn-sm btn-primary" to={item.action_href ?? "/discussions"}>Continue</Link>
    </div>
  );
}

function EmptyRow({ children }: { children: string }) {
  return <div className="attn"><div className="row-meta" style={{ margin: 0, flex: 1 }}>{children}</div></div>;
}

function actionTone(type: string): { pill: string; primary: boolean } {
  const normalized = type.toLowerCase();
  if (normalized.includes("vote")) return { pill: "pill-bad", primary: true };
  if (normalized.includes("conflict")) return { pill: "pill-warn", primary: false };
  if (normalized.includes("review") || normalized.includes("view")) return { pill: "pill-sky", primary: false };
  return { pill: "pill-warn", primary: false };
}

function pillForKind(kind: string) {
  const normalized = kind.toLowerCase();
  if (normalized.includes("legisl")) return "pill-warn";
  if (normalized.includes("proposal") || normalized.includes("vote")) return "pill-warn";
  if (normalized.includes("question")) return "pill-sky";
  if (normalized.includes("topic")) return "pill-neutral";
  return "pill-neutral";
}

function tabForView(view: string | null): Tab {
  if (view === "drafts") return "drafts";
  if (view === "following" || view === "saved") return "following";
  if (view === "proc" || view === "procurations") return "proc";
  return "actions";
}

function relativeAge(time: number) {
  const abs = Math.abs(time - Date.now());
  const hour = 60 * 60 * 1000;
  if (abs < hour) return `${Math.max(1, Math.round(abs / (60 * 1000)))}m ago`;
  if (abs < 24 * hour) return `${Math.max(1, Math.round(abs / hour))}h ago`;
  return `${Math.max(1, Math.round(abs / (24 * hour)))}d ago`;
}
